import React, { useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import SecondaryTabMenuPicker from "../components/common/SecondaryTabMenuPicker";
import useBackNavigation from "../components/navigation/useBackNavigation";
import useHistoryScope from "../components/history/useHistoryScope";
import { HistoryToolbarProvider } from "../components/history/HistoryToolbar";

/**
 * The History screen — everything that has happened to a person, behind one
 * tab strip. Serves all three shells; `useHistoryScope` reads the pathname to
 * decide whose records these are and which tabs exist (Timeline needs a single
 * subject, so the cross-client view doesn't have it).
 *
 * Tab state lives in the URL, one child route each — same as `ProfileScreen`,
 * and necessary here because every listing has detail pages nested under its
 * tab segment, so the tab has to be addressable.
 *
 * The tab line doubles as the page toolbar: tabs portal their "Clear Filters"
 * pill and their own buttons into the two slots on its right, so applying a
 * filter never shifts the table down.
 */

type HistoryTabSegment =
  | "assessments"
  | "programming"
  | "prescription"
  | "timeline";

const TABS: { label: string; segment: HistoryTabSegment }[] = [
  { label: "Assessments", segment: "assessments" },
  { label: "Programming", segment: "programming" },
  { label: "Prescription", segment: "prescription" },
];

const TIMELINE_TAB: { label: string; segment: HistoryTabSegment } = {
  label: "Timeline",
  segment: "timeline",
};

const HistoryScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { basePath, showTimeline } = useHistoryScope();

  // Callback refs into state, so the portalling children re-render once the
  // slots exist (a plain ref wouldn't trigger that render).
  const [filtersSlot, setFiltersSlot] = useState<HTMLElement | null>(null);
  const [actionsSlot, setActionsSlot] = useState<HTMLElement | null>(null);
  const toolbarSlots = useMemo(
    () => ({ filters: filtersSlot, actions: actionsSlot }),
    [filtersSlot, actionsSlot]
  );

  const tabs = showTimeline ? [...TABS, TIMELINE_TAB] : TABS;

  /**
   * A detail page is not a tab, so it hides the tab line.
   *
   * Reuses the rule that already produces the back link — the last path
   * segment being one of the route's params — so "this is a record, not a
   * listing" is decided in one place. The breadcrumb still names the tab the
   * record came from, and the back link returns to it, so no context is lost.
   *
   * The row carries the toolbar slots too, which is correct: "Clear Filters"
   * and the listing actions belong to a listing. A tab portalling into a slot
   * that isn't there renders nothing.
   */
  const isDetailPage = Boolean(useBackNavigation());

  // The tab segment is the one right after the base path — detail routes add
  // further segments (`…/assessments/lab/42`) and must keep their tab active.
  const currentSegment = location.pathname
    .slice(basePath.length)
    .split("/")
    .filter(Boolean)[0];
  const activeIndex = Math.max(
    0,
    tabs.findIndex((tab) => tab.segment === currentSegment)
  );

  const onTabChange = (index: number) => {
    const target = tabs[index];
    if (!target) return;
    // Absolute, not "replace the last segment": we may be several segments deep
    // in a detail page. `location.search` carries `?id=` and the chat mode.
    navigate(`${basePath}/${target.segment}${location.search}`);
  };

  return (
    <Box sx={styles.container}>
      {!isDetailPage && (
        <Box sx={styles.tabRow}>
          <SecondaryTabMenuPicker
            tabs={tabs.map((tab, index) => ({ label: tab.label, key: index }))}
            selectedTab={activeIndex}
            // SecondaryTabMenuPicker reports the array index, not `tab.key`.
            setSelectedTab={onTabChange}
            isFullWidth={false}
          />
          <Box sx={styles.toolbarSpacer} />
          <Box ref={setFiltersSlot} sx={styles.toolbarSlot} />
          <Box ref={setActionsSlot} sx={styles.toolbarSlot} />
        </Box>
      )}
      <Box sx={styles.content}>
        <HistoryToolbarProvider value={toolbarSlots}>
          <Outlet />
        </HistoryToolbarProvider>
      </Box>
    </Box>
  );
};

export default HistoryScreen;

const styles = {
  // Bounded to the viewport, like `ServicesLayout` and `PricingScreen`: the
  // shells' content column is `100vh` with `overflow: auto` and does not
  // constrain its children, so a screen that only says `flex: 1` grows with
  // its rows and the whole page scrolls. 96px is the breadcrumb row plus the
  // content padding. With the screen bounded, each listing's own
  // `ScrollableWrapper` is the element that scrolls — which the load-more
  // trigger and the header/tab strip staying put both rely on.
  container: {
    display: "flex",
    flexDirection: "column" as "column",
    width: "100%",
    height: "calc(100vh - 96px)",
    minHeight: 0,
    boxSizing: "border-box" as "border-box",
  },
  tabRow: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    gap: "16px",
    marginBottom: "16px",
    // The tab underline is measured from the DOM and the strip must keep its
    // intrinsic width, so the toolbar is pushed right by a spacer rather than
    // by stretching the strip.
    minHeight: "40px",
  },
  toolbarSpacer: {
    flex: 1,
    minWidth: 0,
  },
  toolbarSlot: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    flexShrink: 0,
  },
  content: {
    display: "flex",
    flexDirection: "column" as "column",
    flex: 1,
    minHeight: 0,
  },
};
